import {
  FilterCodecs,
  useFilterParam,
  type FilterCodec,
} from "@freightsimple/ui-core/Hooks/useFilterParam";
import { CompanyWebhookEventType } from "@freightsimple/generated-dashboard-openapi-client/models/CompanyWebhookEventType";
import { WebhookDeliveryState } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookDeliveryState";
import { asLocalDate } from "@freightsimple/time/branded-types";
import { isLocalDate } from "@freightsimple/time/helpers";
import type { LocalDate } from "@freightsimple/time/branded-types";

/** "Any" for the enum filters: the URL carries nothing and the server gets no predicate. */
export const WEBHOOKS_FILTER_ANY = "";

const eventTypeValues = [
  WEBHOOKS_FILTER_ANY,
  ...Object.values(CompanyWebhookEventType),
] as const;
const stateValues = [
  WEBHOOKS_FILTER_ANY,
  ...Object.values(WebhookDeliveryState),
] as const;

/**
 * A calendar day from the URL, or empty. A hand-edited `?from=bogus` would otherwise reach the
 * date conversion on every fetch and keep the list from ever loading.
 */
const localDateCodec: FilterCodec<string> = {
  serialize: (value) => (value === "" ? undefined : value),
  deserialize: (raw) => (isLocalDate(raw) ? raw : ""),
};

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A webhook id from the URL, or empty: a malformed one would be a 400 on every fetch. */
const webhookIdCodec: FilterCodec<string> = {
  serialize: (value) => (value === "" ? undefined : value),
  deserialize: (raw) => (UUID_PATTERN.test(raw) ? raw : ""),
};

export interface WebhooksDeliveriesFilter {
  webhookId: string;
  eventType: CompanyWebhookEventType | typeof WEBHOOKS_FILTER_ANY;
  state: WebhookDeliveryState | typeof WEBHOOKS_FILTER_ANY;
  /** Both local calendar days, inclusive. Either may be empty. */
  from: LocalDate | undefined;
  to: LocalDate | undefined;
}

/**
 * The delivery list's filters, kept in the URL so a customer can hand a colleague the exact view
 * they are looking at, and so a page reload after a redeliver lands back on it.
 */
export function useWebhooksDeliveriesFilters() {
  const [webhookId, setWebhookId] = useFilterParam(
    "webhook",
    "",
    webhookIdCodec,
  );
  const [eventType, setEventType] = useFilterParam(
    "event",
    WEBHOOKS_FILTER_ANY,
    FilterCodecs.oneOf(eventTypeValues, WEBHOOKS_FILTER_ANY),
  );
  const [state, setState] = useFilterParam(
    "state",
    WEBHOOKS_FILTER_ANY,
    FilterCodecs.oneOf(stateValues, WEBHOOKS_FILTER_ANY),
  );
  const [from, setFrom] = useFilterParam("from", "", localDateCodec);
  const [to, setTo] = useFilterParam("to", "", localDateCodec);

  const filter: WebhooksDeliveriesFilter = {
    webhookId,
    eventType,
    state,
    from: from ? asLocalDate(from) : undefined,
    to: to ? asLocalDate(to) : undefined,
  };

  function setDateRange(range: [LocalDate, LocalDate | undefined] | undefined) {
    setFrom(range?.[0] ?? "");
    setTo(range?.[1] ?? "");
  }

  return {
    filter,
    setWebhookId,
    setEventType,
    setState,
    setDateRange,
    /** Changes whenever the result set would, so the table returns to page 1. */
    resetPageKey: JSON.stringify([webhookId, eventType, state, from, to]),
  };
}
